// Pure, dependency-free per-ad x per-audience-segment aggregation for the creative report.
// Lives in plain .js (no @/ aliases, no JSX) so it is unit-testable via `node --test`.
// calculateInsights is injected by the caller (ads.jsx) to keep this file dep-free.
//
// Unlike the group-by report (which buckets ADS by an ad property), this keeps BOTH
// axes: for every ad we fold its Meta age,gender insight rows into one entity per
// (ad, segment). That per-cell structure is what powers "which ad worked for which
// audience" — the matrix (ads x segments) and the per-audience ranked list both read it.
//
// The segment axis is pluggable (AUDIENCE_DIMENSIONS): Meta age,gender (default) or the
// Advantage+ user_segment_key (new / engaged / existing customers).

import { userSegmentKeyOf, userSegmentLabel } from './audienceSegment.js'

const AGE_ORDER = ['13-17', '18-24', '25-34', '35-44', '45-54', '55-64', '65+', 'unknown']
const GENDER_ORDER = ['female', 'male', 'unknown']

// Compose the segment key off a broken-down insight row. Only real demographic
// segments count — rows with an unknown/missing gender or age are dropped entirely
// (no "unknown" values or column), so they don't appear and don't inflate totals.
export const segmentKeyOf = (row) => {
    const gender = (row?.gender || '').toLowerCase()
    const age = row?.age
    // Keep every segment, INCLUDING 'unknown' gender/age — only skip a row with no
    // demographic split at all. Unknown-missing halves default to 'unknown' so the
    // matrix totals reconcile with the account (unknown shown as its own segment).
    if (!gender && !age) {
        return null
    }
    return `${gender || 'unknown'}·${age || 'unknown'}`
}

const segmentSortTuple = (gender, age) => {
    const g = GENDER_ORDER.indexOf(gender)
    const a = AGE_ORDER.indexOf(age)
    return [g === -1 ? 99 : g, a === -1 ? 99 : a]
}

const USER_SEGMENT_ORDER = ['new', 'new_audience', 'prospecting', 'engaged', 'engaged_audience', 'existing', 'existing_customer', 'existing_customers']

const userSegmentRank = (key) => {
    if (key === 'unknown') {
        return 999
    }
    const i = USER_SEGMENT_ORDER.indexOf(key)
    return i === -1 ? 500 : i
}

// Each dimension: the Meta breakdowns param, how to key a row, the segment's descriptive
// meta (label + optional gender/age used by the age·gender chips), and a sort comparator.
export const AUDIENCE_DIMENSIONS = {
    age_gender: {
        breakdown: 'age,gender',
        label: 'Gender × age',
        keyOf: segmentKeyOf,
        metaOf: (key, row) => {
            const gender = (row.gender || 'unknown').toLowerCase()
            const age = row.age || 'unknown'
            return { key, gender, age, label: `${gender} · ${age}` }
        },
        compare: (a, b) => {
            const [ag, aa] = segmentSortTuple(a.gender, a.age)
            const [bg, ba] = segmentSortTuple(b.gender, b.age)
            return ag - bg || aa - ba
        },
    },
    user_segment: {
        breakdown: 'user_segment_key',
        label: 'Audience segment',
        keyOf: userSegmentKeyOf,
        metaOf: (key) => {
            const label = userSegmentLabel(key)
            return { key, label, shortLabel: label }
        },
        compare: (a, b) => userSegmentRank(a.key) - userSegmentRank(b.key) || a.label.localeCompare(b.label),
    },
}

export const DEFAULT_AUDIENCE_DIMENSION = 'age_gender'

export const buildAdAudienceMatrix = (adsData, calculateInsights, opts = {}) => {
    const { attributionWindowKeys, benchmark, internalCustomMetrics, platform = 'meta', dimension = DEFAULT_AUDIENCE_DIMENSION } = opts
    const dim = AUDIENCE_DIMENSIONS[dimension] || AUDIENCE_DIMENSIONS[DEFAULT_AUDIENCE_DIMENSION]

    const cells = {}          // cells[adId][segmentKey] = entity (this ad, this segment)
    const adTotals = {}       // adTotals[adId]           = entity across all segments (row total)
    const segmentTotals = {}  // segmentTotals[segKey]    = entity across all ads (column total)
    const adsById = {}
    const adsOrder = []
    const segmentMeta = {}    // segKey -> { key, label, ...dimension-specific fields }

    for (const ad of adsData || []) {
        const adId = ad?.id
        if (!adId) {
            continue
        }
        const adPlatform = ad._provider || platform
        const insights = Array.isArray(ad.insights) ? ad.insights : []

        for (const insight of insights) {
            const segKey = dim.keyOf(insight)
            if (!segKey) {
                continue
            }

            if (!adsById[adId]) {
                // Keep a slim copy of the source ad (minus the big insights array) so the
                // row can open the real CreativeView (needs ad id + descriptive fields).
                const { insights: _omit, ...sourceAd } = ad
                adsById[adId] = {
                    id: adId,
                    name: ad.name,
                    title: ad.name,
                    creatives: ad.creatives || [],
                    adType: ad.adType,
                    sourceAd,
                }
                adsOrder.push(adId)
                cells[adId] = {}
                adTotals[adId] = {}
            }
            if (!segmentMeta[segKey]) {
                segmentMeta[segKey] = dim.metaOf(segKey, insight)
                segmentTotals[segKey] = {}
            }
            if (!cells[adId][segKey]) {
                cells[adId][segKey] = {}
            }

            // Fold this insight into the cell, the ad's row total, and the segment's column total.
            // calculateInsights mutates + returns the target and only reads the insight, so folding
            // the same row into three accumulators is safe (mirrors buildAssetBreakdownGroups).
            calculateInsights(cells[adId][segKey], insight, attributionWindowKeys, benchmark, internalCustomMetrics, adPlatform)
            calculateInsights(adTotals[adId], insight, attributionWindowKeys, benchmark, internalCustomMetrics, adPlatform)
            calculateInsights(segmentTotals[segKey], insight, attributionWindowKeys, benchmark, internalCustomMetrics, adPlatform)
        }
    }

    // Canonical segment order per dimension (age·gender: gender then age bucket).
    const segments = Object.values(segmentMeta).sort(dim.compare)

    const ads = adsOrder.map(id => ({ ...adsById[id], total: adTotals[id] }))

    return { ads, segments, cells, adTotals, segmentTotals }
}
